/**
 * Zabudovaný obsah. Neskôr (Fáza 7) pribudnú mapy z databázy — vtedy sa
 * `buildContentIndex` zavolá so spojením zabudovaných a DB máp.
 */

import type { CardDef, CardDefId, ContentIndex, DeckDef, GameMap, HeroDef } from '@unmatched/engine';

import umdb from '../generated/umdb.json' with { type: 'json' };
import berserkDeck from '../decks/berserk.json' with { type: 'json' };
import hranicarDeck from '../decks/hranicar.json' with { type: 'json' };
import heroesJson from '../heroes/heroes.json' with { type: 'json' };
import klastor from '../maps/klastor.json' with { type: 'json' };
import pristav from '../maps/pristav.json' with { type: 'json' };
import testArena from '../maps/test-arena.json' with { type: 'json' };
// Prepisy skutočných dosiek (obrázok dosky v uploads/boards, cez /api/boards/<id>/image).
import baskervilleManor from '../maps/baskerville-manor.json' with { type: 'json' };
import fayrlundForest from '../maps/fayrlund-forest.json' with { type: 'json' };
import marmoreal from '../maps/marmoreal.json' with { type: 'json' };
import sarpedon from '../maps/sarpedon.json' with { type: 'json' };
import streetsOfNovigrad from '../maps/streets-of-novigrad.json' with { type: 'json' };
import { deckSchema, heroSchema, mapSchema } from './schema.js';

export * from './schema.js';
export * from './i18n.js';

export function buildContentIndex(input: {
  maps: unknown[];
  heroes: unknown[];
  decks: unknown[];
}): ContentIndex {
  const maps: Record<string, GameMap> = {};
  for (const raw of input.maps) {
    const map = mapSchema.parse(raw) as GameMap;
    maps[map.id] = map;
  }

  const heroes: Record<string, HeroDef> = {};
  for (const raw of input.heroes) {
    const hero = heroSchema.parse(raw) as HeroDef;
    heroes[hero.id] = hero;
  }

  const decks: Record<string, DeckDef> = {};
  const cards: Record<string, CardDef> = {};
  for (const raw of input.decks) {
    const deck = deckSchema.parse(raw) as DeckDef;
    decks[deck.id] = deck;
    for (const card of deck.cards) cards[card.id] = card;
  }

  for (const hero of Object.values(heroes)) {
    if (!decks[hero.deckId]) throw new Error(`Hrdina ${hero.id} odkazuje na neznámy balíček ${hero.deckId}`);
  }

  return { maps, heroes, decks, cards };
}

/** Obsah dodávaný s aplikáciou. */
/**
 * Zabudované postavy = dvojica testovacích (na ladenie enginu) + import
 * z unmatched.cards. Import je generovaný, viď generated/ATTRIBUTION.md.
 */
export const content: ContentIndex = buildContentIndex({
  maps: [testArena, klastor, pristav, marmoreal, sarpedon, baskervilleManor, fayrlundForest, streetsOfNovigrad],
  heroes: [...heroesJson, ...umdb.heroes],
  decks: [berserkDeck, hranicarDeck, ...umdb.decks],
});

export const DEFAULT_MAP_ID = 'test-arena';
export const DEFAULT_HEROES = ['berserk', 'hranicar'];

/**
 * Meno figúrky podľa jej ID. Karty nesú v `character` ID hrdinu alebo sidekicka
 * (banner „kto ju smie zahrať"); na karte má stáť jeho meno, nie slug.
 * `null` = ktorákoľvek vlastná figúrka, čo sa na karte píše ako ANY.
 */
const FIGHTER_NAMES = new Map<string, string>();
for (const hero of Object.values(content.heroes)) {
  FIGHTER_NAMES.set(hero.id, hero.name);
  for (const sidekick of hero.sidekicks ?? []) FIGHTER_NAMES.set(sidekick.id, sidekick.name);
}

export function fighterLabel(defId: string | null | undefined): string | null {
  if (!defId) return null;
  return FIGHTER_NAMES.get(defId) ?? defId;
}

/**
 * Portrét postavy. Zdroj má obrázky **len ku kartám**, nie k hrdinom, ale na
 * karte je nakreslená práve tá postava, ktorá ju smie zahrať — tak sa portrét
 * vezme z jej vlastnej karty (klient ju načíta cez `/api/cards/:id/image`
 * a oreže na hornú časť, kde je obraz). Výber je deterministický, aby sa
 * portrét medzi zobrazeniami nemenil.
 */
export function portraitCard(defId: string | null | undefined): CardDefId | null {
  if (!defId) return null;
  const hero = content.heroes[defId];
  const deckId =
    hero?.deckId ??
    Object.values(content.heroes).find((h) => h.sidekicks?.some((s) => s.id === defId))?.deckId;
  const cards = deckId ? (content.decks[deckId]?.cards ?? []) : [];
  const withArt = cards.filter((card) => card.image);
  const own = withArt.filter((card) => card.character === defId);
  return (own[0] ?? withArt[0])?.id ?? null;
}
